<template>
    <ion-item :lines="lines" class="input-simple-container">
        <ion-label v-if="name || $slots.default" :position="position">
            <slot>
                {{ name }}
            </slot>
        </ion-label>

        <ion-select
            class="select-base" :cancel-text="cancelText" :compareWith="compareWith" :disabled="disabled"
            :interface="interface" :interface-options="interfaceOptions" :mode="mode" :multiple="multiple" :name="name"
            :ok-text="okText" :placeholder="placeholder" :selected-text="selectedText" :value="modelValue"
            @ion-change="handlerChange" @ion-blur="handleBlur"
        >
            <slot name="options">
                <template v-if="parsedOptions">
                    <template v-for="option of parsedOptions" :key="option.value">
                        <ion-select-option :value="option.value">{{ option.text }}</ion-select-option>
                    </template>
                </template>
            </slot>
        </ion-select>

        <ion-label v-if="errors && errors.length > 0" slot="error" class="base-input-error">
            <span v-for="error of errors" :key="error">
                {{ error }}
            </span>
        </ion-label>

        <ion-label v-if="$slots['helper']" slot="helper">
            <slot name="helper"/>
        </ion-label>
    </ion-item>
</template>

<script setup lang="ts">
import { IonItem, IonLabel, IonSelect, IonSelectOption, SelectCustomEvent } from "@ionic/vue";
import { computed } from "vue";
import { useField } from "vee-validate";
import { t } from "@/plugins/vue-i18n";

import { getOptions } from "@/functions/converter";

import { SelectBaseProps } from "@/types/props";
import { SelectBaseEmits } from "@/types/emits";
import { ION_MODE, ION_SELECT_INTERFACE, LABEL_POSITION } from "@/types/enum/ionicEnum";
import { SelectOption } from "@/types/others";

const props = withDefaults(defineProps<SelectBaseProps>(), {
    cancelText: t('button.cancel') ?? 'Abbrechen',
    okText: t('button.ok') ?? 'Ok',
    valueKey: 'value',
    textKey: 'text',
    lines: 'none',
    position: LABEL_POSITION.STACKED,
    placeholder: 'Auswählen',
    interface: ION_SELECT_INTERFACE.ACTION_SHEET,
    interfaceOptions: () => {
        return { mode: ION_MODE.IOS }
    }
})

const emit = defineEmits<SelectBaseEmits>()

// Vee-Validate Composable
const {
    name,
    errors,
    handleChange,
    handleBlur,
    validate
} = useField(
    props.validateOption?.name ?? '',
    props.validateOption?.rules ?? '',
    {
        valueProp: props.modelValue,
        initialValue: JSON.parse(JSON.stringify(props.modelValue ?? '')),
        label: props.validateOption?.label ?? props.validateOption?.name,
        ...props.validateOption?.options
    }
)

const parsedOptions = computed<SelectOption[]>(() => {
    if (!props.options) return []
    const textKey = props.textKey
    const valueKey = props.valueKey
    return getOptions(props.options, valueKey, textKey)
})

const handlerChange = (event: SelectCustomEvent): void => {
    emit('update:modelValue', event.detail.value);
    handleChange(event);
    emit('change', event.detail.value)
}

window.addEventListener('change-language', () => validate({ mode: 'force' }))
</script>

<style>
.select-base::part(icon) {
    transform: none;
}
</style>
